import {
  getElementData,
  expandComparisonFamilies,
  extractComparisons,
  extractResults,
  extractResponses,
  PRODUCT_KEYS as S,
} from "./data_helpers.js";
import { topSorted, reduceToObject } from "./shared.js";
import { useMemo } from "react";

// IMPORTANT: deliberately hardcoded here despite elsewhere,
// this is needed so that subgroup redaction happens in new UI.
const INCLUDE_NA_GROUPS = false;

export const [E, appData] = (() => {
  let appData;
  if (typeof document !== "undefined") {
    appData = getElementData("#app_data", "data");
    window.dbgAppData = appData;
  } else {
    // HT: https://stackoverflow.com/a/38056891/179583
    // and https://stackoverflow.com/q/22233950/179583
    let nodeRequire = require, // ~HACK: bypass browserify
      fs = nodeRequire("fs");
    let dataJSON = fs.readFileSync("/dev/stdin");
    appData = JSON.parse(dataJSON);
  }

  let { survey } = appData;
  survey._is4Coaches = survey.product_key == S.BEL4T;
  survey.list.forEach((year, idx) => {
    year._item_index = idx;
  });
  let full_list = survey.list.filter((d) => d.has_measures);
  // DEV: help test some various graph cases against 4-survey mock data
  if (0) full_list[2].school_year = full_list[0].school_year;
  if (0) full_list[3].school_year = full_list[0].school_year;
  if (0) full_list = full_list.slice(1);
  full_list.forEach((year, idx) => {
    year._score_index = idx;
    year._prior = idx > 0;
  });
  survey._fullYears = full_list.slice(0).reverse();
  survey._fullYears.forEach((year, idx) => {
    year._result_index = idx;
    year._responses_index = year.index;
  });
  survey._mainYear = full_list[0];
  survey._mainYear._main = true;

  // also group up years found
  survey._schoolYears = [];
  let _yearGroup;
  survey._fullYears.forEach((year) => {
    if (year.school_year !== _yearGroup?._school_year) {
      survey._schoolYears.push((_yearGroup = []));
      _yearGroup._school_year = year.school_year;
    }
    _yearGroup.push(year);
  });
  survey._schoolYears.forEach((yearGroup, idx) => {
    let revIdx = survey._schoolYears.length - 1 - idx;
    yearGroup._reverseIdx = revIdx;
    yearGroup._idx = idx;
    if (revIdx === 0) {
      yearGroup._label = "Current Year";
    } else if (revIdx === 1) {
      yearGroup._label = "Last Year";
    } else {
      yearGroup._label = yearGroup._school_year;
    }
  });

  // ~HACK: no ranking, but graphs still have tick lines every 20%
  // (and trends graph wants to set up gradient between possible colors…)
  const _RANK = reduceToObject(appData.survey_ranks, (d) => [d.code, d]);
  let r0 = 0;
  let rankRanges = [20, 40, 60, 80, 100].map((r1) => {
    return { rank: _RANK["BEL"], range: [r0, (r0 = r1)] };
  });
  appData.allRanks = appData.survey_ranks;

  appData.responseInfo = appData.response_history;

  let resultCtx = appData;
  if (appData.comparisons) {
    appData.comparisonFamilies = expandComparisonFamilies(appData.comparisons);
  }
  resultCtx.scoreToRankCode = (s) => {
    let code = s == null ? "X" : "BEL";
    return [code, _RANK[code]];
  };

  // HACK: make this more conveniently available to hooks below
  appData.essentials._learningConditionSlug = appData.lc_slug;
  appData.essentials._learningBeliefsSlug = appData.lb_slug;

  appData.essentials.forEach((e) => {
    e._level = "essentials";
    e._results = extractResults(resultCtx, "all", e);
    e.measures = appData.measures.filter((d) => d.parent_slug === e.slug);
  });
  appData.measures.forEach((m) => {
    m._level = "measures";
    m._results = extractResults(resultCtx, "all", m);
    if (0) {
      // DEV: simulate some missing data situations
      Object.keys(m.data).forEach((k) => {
        if (k.startsWith("english")) {
          m.data[k].forEach((d) => {
            d[0] = null;
          });
        }
        if (k.startsWith("science")) {
          m.data[k].slice(0, -1).forEach((d) => {
            d[0] = null;
          });
        }
        if (k === "social") {
          m.data[k].slice(1).forEach((d) => {
            d[0] = null;
          });
        }
        if (survey._is4Coaches && k === "boys") {
          m.data[k].forEach((d) => {
            d[0] = null;
          });
        }
        if (k === "amerind") {
          m.data[k].slice(0, -1).forEach((d) => {
            d[0] = null;
          });
        }
        if (k === "mixed") {
          m.data[k].forEach((d) => {
            d[0] = null;
          });
        }
      });
    }
    // simulate completely missing gender data
    if (0) {
      const fam = appData.comparisonFamilies.measures.bySlug["gender"];
      const gk = fam?.variants.map((opt) => opt.slug) ?? [];
      Object.keys(m.data).forEach((k) => {
        if (gk.includes(k))
          m.data[k].forEach((d) => {
            d[0] = null;
          });
      });
    }
    // provide some teacher data
    if (0) {
      Object.keys(m.data).forEach((k) => {
        if (k === "period1") {
          m.data[k].slice(1).forEach((d) => {
            d[0] = 42;
          });
        }
        if (k === "period2" || k === "period4") {
          m.data[k].forEach((d) => {
            d[0] = Math.random() * 100;
          });
        }
      });
    }
    if (appData.comparisons) {
      m._comparisons = extractComparisons(
        {
          ...resultCtx,
          // ~HACK: new UI wants to show rows for missing combos…
          includeDatalessGroups: true,
        },
        "measures",
        m,
      );
    }
    m.items = appData.items.filter((d) => d.parent_slug === m.slug);
    m._resources = appData.resource_templates?.map((d) => ({
      ...d,
      url: d.url_template.replace("_PLACEHOLDER_", m.slug),
    }));
  });
  appData.items.forEach((i) => {
    i._responses = extractResponses(resultCtx, i);

    // ~HACK: client wants special labelling ability for new appendix
    const _sep = ": ";
    if (i.prompt.includes(_sep)) {
      let [head, ...rest] = i.prompt.split(_sep);
      i._xtraHeading = head;
      i.prompt = rest.join(_sep);
    }
  });

  if (appData.school_data)
    appData.schools = appData.school_data.map(
      ({ name, slug, url, data: sdata }) => {
        let responseInfo = sdata.response_history;
        let measuresBySlug = reduceToObject(sdata.measures, (d) => [
          d.slug,
          {
            _level: "measures",
            _results: extractResults(resultCtx, "all", d),
            ...d,
          },
        ]);
        return { name, slug, url, responseInfo, measuresBySlug };
      },
    );

  let enums = {
    rankRanges,
  };

  return [enums, appData];
})();

export const useLearningConditions = (essentials, allMeasures, year = null) => {
  const yearIdx = year ? year._result_index : "latest";
  const lcSlug = essentials._learningConditionSlug;
  const essential = useMemo(
    () => essentials.find((d) => d.slug === lcSlug),
    [essentials, lcSlug],
  );

  const measures = useMemo(() => {
    const lcMeasures = allMeasures.filter((d) => d.parent_slug === lcSlug);
    if (0) {
      // DEV: make sure 9 measures like expected with production dataset
      while (lcMeasures.length < 9) lcMeasures.push(...lcMeasures);
      lcMeasures.length = 9;
    }
    return topSorted(lcMeasures, `+score@${yearIdx}`, null);
  }, [allMeasures, lcSlug, yearIdx]);

  return [essential, measures];
};

export const useLearningBeliefs = (essentials, measures) => {
  const lbSlug = essentials._learningBeliefsSlug;
  const essential = useMemo(
    () => essentials.find((d) => d.slug === lbSlug),
    [essentials, lbSlug],
  );
  const supplementalMeasures = useMemo(
    () => measures.filter((d) => d.parent_slug === "_supplemental"),
    [measures],
  );
  return [essential, supplementalMeasures];
};

function extractTabs(survey, measure, pdfMode) {
  let _comps = measure._comparisons;
  let mainTab = {
    slug: "multifactor",
    label: null, // determined below
    options: [],
  };
  let otherTab = {
    slug: "demographic",
    label: "By Demographic",
    options: [],
  };
  let famSet = _comps.byFamily,
    haveGrades = "grade" in famSet,
    haveSubjects = "subject" in famSet,
    haveClassrooms = "class_period" in famSet,
    haveMultifactor = "_multifactor_" in famSet;
  if (survey._is4Coaches && haveClassrooms) {
    mainTab.label = "By Class Period";
  } else if (haveGrades && haveSubjects) {
    mainTab.label = "By Grade & Subject";
  } else if (haveGrades) {
    mainTab.label = "By Grade";
  } else if (haveSubjects) {
    mainTab.label = "By Subject";
  } else {
    mainTab.label = "Overall";
  }

  const mainIdx = survey._mainYear._result_index;
  function classifyAvailability(opt) {
    let hasMainData = false;
    let hasAnyData = false;
    let _objs = opt._availabilityObjects || opt.soloObjects || opt.objects;
    _objs.forEach((obj) => {
      hasMainData ||= obj._results[mainIdx].score != null;
      hasAnyData ||= obj._results.some((d) => d.score != null);
    });
    Object.assign(opt, { hasMainData, hasAnyData });
  }

  function gatherComparableOption(slug) {
    let _subComps = _comps.byComparison[slug];
    // `famInfo` is the primary disaggregation family
    let famInfo = _comps.families.find((d) => d.slug === slug);
    // `altInfo` is the secondary multifactor/cross-comparison sub-family
    // NOTE: we assume backend gives us exactly one option if there's _subComps!
    let altInfo = _subComps?.optionFamilies[0];

    let mainOption = {
      slug: "_all",
      // ~HACK: assume simple plural works for all families
      label: `All ${altInfo?.name ?? "Student"}s`,
      objects: _comps.byFamily[slug],
    };
    let subOptions = [];
    if (haveMultifactor && !pdfMode) {
      subOptions = altInfo.variants.map(({ slug, label, option }) => ({
        slug,
        label: option ?? label, // subject/grade have special label variants
        objects: _subComps.byDisaggregation[slug],
      }));
      if (!INCLUDE_NA_GROUPS) {
        // don't show broken options if the data helpers left them undefined
        // NOTE: when passing `includeDatalessGroups` option, all comparable groups should now have objects anyway
        subOptions = subOptions.filter((opt) => Array.isArray(opt.objects));
      }
    }

    return {
      slug,
      label: famInfo.name,
      options: [mainOption, ...subOptions],
    };
  }

  if (haveClassrooms) {
    mainTab.options.push(gatherComparableOption("class_period"));
  }
  if (haveGrades) {
    mainTab.options.push(gatherComparableOption("grade"));
  }
  if (haveSubjects) {
    mainTab.options.push(gatherComparableOption("subject"));
  }
  if (pdfMode) {
    // ~HACK: this is all the PDF needs…
    return mainTab.options;
  }
  // the rest go into an alternate tab (and aren't comparable between options)
  _comps.families
    .filter(
      (d) =>
        d.slug != "subject" && d.slug != "grade" && d.slug !== "class_period",
    )
    .forEach(({ slug, name: label, variants }) => {
      let options = variants.map(({ slug, label, option }) => ({
        slug,
        label: option ?? label,
        soloObjects: [_comps.byVariant[slug]],
      }));
      if (!INCLUDE_NA_GROUPS) {
        // don't show empty options which the data helpers left out
        // NOTE: even when passing `includeDatalessGroups` some incomparable groups still get redacted!
        let objects = _comps.byFamily[slug];
        if (objects.__vslug_set) {
          options = options.filter(({ slug }) => slug in objects.__vslug_set);
        }
      }
      // NOTE: UI code expects every family group to have at least one option!
      if (options.length) {
        otherTab.options.push({ slug, label, options });
      }
    });

  // (this is last because for a little while it could end up in `otherTab`)
  mainTab.options.push({
    slug: "_all_",
    label: "Overall",
    options: [
      {
        slug: "_all",
        label: "All Students",
        objects: [],
        _availabilityObjects: [measure],
      },
    ],
  });

  let tabs = [mainTab];
  if (mainTab !== otherTab && otherTab.options.length) {
    tabs.push(otherTab);
  }
  tabs.forEach((tab) => {
    tab.options.forEach((cmp) => {
      cmp.options.forEach((opt) => {
        classifyAvailability(opt);
      });
    });
  });
  return tabs;
}

export const useDisaggregations = (survey, measure, mode = "normal") => {
  const pdfMode = mode === "pdf";
  return useMemo(() => extractTabs(survey, measure, pdfMode), [measure.slug]);
};

export const useIntroYears = (survey, activeYear) => {
  // n.b. we assume all underlying year objects are shared
  const introYears = useMemo(
    () =>
      survey._fullYears.filter(
        (sy) => sy.school_year === survey._mainYear.school_year,
      ),
    [survey],
  );
  const fallYear =
    introYears[0].survey_type === "cultivate_fall" ? introYears[0] : null;
  const alternateYear =
    activeYear._main && fallYear != activeYear ? fallYear : null;
  return [introYears, alternateYear];
};
